// El autenticador (Microsoft Authenticator / Google Authenticator).
//
// Auditoría de seguridad 2026-09-27: a una agencia le hackearon el correo, y el
// único segundo paso de Deskte era un código POR CORREO — quien tiene el correo
// tiene el código. Con la app autenticadora el código sale del TELÉFONO de la
// persona, y el correo robado ya no basta.
//
// Dos piezas:
//   - AutenticadorPanel: en Configuración → Seguridad. Activar (QR + primer
//     código + 10 códigos de recuperación que se ven UNA vez) y quitarlo.
//   - AutenticadorCodigoScreen: la pantalla que AuthGate pone delante del CRM
//     cuando la cuenta lo tiene activo y esta sesión aún no dio el código.
//
// El servidor es quien decide (deskte-api utils/autenticador.ts); esto solo
// pregunta y enseña.

import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, Smartphone, KeyRound, Copy, Download } from 'lucide-react';
import { getMfaStatus, startMfaEnroll, confirmMfaEnroll, verifyMfa, disableMfa } from '../api.js';
import { QrDelEnlace } from './MyDigitalCard.jsx';

const soloDigitos = (v) => String(v || '').replace(/\D/g, '').slice(0, 6);

/** El mensaje que se enseña para un error del servidor, en el idioma de quien mira. */
function mensajeDeError(e, es) {
  if (e?.code === 'MFA_LOCKED') {
    const min = Math.max(1, Math.ceil((e.retryAfterSec || 900) / 60));
    return es ? `Demasiados intentos. Espera ${min} min.` : `Too many attempts. Wait ${min} min.`;
  }
  // Un código bueno que ya se gastó (la sesión cambió dentro del mismo medio
  // minuto). Antes salía "Código incorrecto" siendo el correcto, y a los cinco
  // intentos quedaba bloqueada (Lissette 2026-09-30). No cuenta como fallo.
  if (e?.code === 'MFA_CODE_USED') {
    return es
      ? 'Ese código ya se usó. Espera a que la app muestre el siguiente (cambia cada 30 segundos) y escríbelo.'
      : 'That code was already used. Wait for the next one (it changes every 30 seconds) and enter it.';
  }
  if (e?.code === 'MFA_BAD_CODE') {
    const quedan = typeof e.remainingAttempts === 'number' ? e.remainingAttempts : null;
    // Casi siempre es la hora del teléfono: la app calcula el código con ella.
    const base = es
      ? 'Código incorrecto. Revisa que la hora del teléfono esté en automático.'
      : 'Wrong code. Check that your phone’s time is set automatically.';
    if (quedan == null) return base;
    return es ? `${base} Te quedan ${quedan} intentos.` : `${base} ${quedan} attempts left.`;
  }
  // El límite de peticiones por minuto, NO intentos fallidos: no gasta ninguno
  // de los 5. Antes salía en inglés ("Too many requests…") y se leía como
  // "metiste el código muchas veces" (Lissette 2026-10-07).
  if (e?.code === 'AUTH_RATE_LIMITED') {
    return es
      ? 'Deskte recibió demasiadas peticiones seguidas (no es por el código). Espera un minuto y vuelve a escribirlo. Si tienes Deskte abierto en varias pestañas, deja solo una.'
      : 'Deskte got too many requests in a row (not because of your code). Wait a minute and try again. If Deskte is open in several tabs, keep just one.';
  }
  if (e?.code === 'SESSION_REPLACED') {
    return es ? 'Tu sesión cambió. Recarga la página.' : 'Your session changed. Reload the page.';
  }
  return e?.message || (es ? 'Algo salió mal.' : 'Something went wrong.');
}

const campoCodigo = {
  width: '100%',
  background: 'var(--c-surface2,#112538)',
  border: '1px solid var(--c-border,#1a3550)',
  borderRadius: 8,
  padding: '10px 14px',
  color: 'var(--c-white,#eef6ff)',
  fontSize: '1.2rem',
  fontFamily: 'var(--font-mono, monospace)',
  letterSpacing: '.35em',
  outline: 'none',
  boxSizing: 'border-box',
  textAlign: 'center',
};
const botonPrincipal = {
  padding: '.65rem 1.1rem',
  borderRadius: 9,
  border: 'none',
  background: 'linear-gradient(135deg, #0099cc, #0055aa)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '.88rem',
  cursor: 'pointer',
};
const botonSecundario = {
  padding: '.55rem 1rem',
  borderRadius: 9,
  border: '1px solid var(--c-border,#1a3550)',
  background: 'transparent',
  color: 'var(--c-text2,#7aa3c0)',
  fontWeight: 600,
  fontSize: '.82rem',
  cursor: 'pointer',
};
const cajaError = {
  background: 'rgba(255,61,107,.08)',
  border: '1px solid rgba(255,61,107,.25)',
  borderRadius: 9,
  padding: '.6rem .8rem',
  color: 'var(--c-red,#ff3d6b)',
  fontSize: '.82rem',
};

/** Los 10 códigos de recuperación, con copiar y descargar. Se ven UNA vez. */
function CodigosDeRecuperacion({ codigos, es, email, onListo }) {
  const [copiado, setCopiado] = useState(false);
  const texto = [
    'Deskte — ' + (es ? 'códigos de recuperación del autenticador' : 'authenticator recovery codes'),
    email || '',
    '',
    ...codigos,
    '',
    es ? 'Cada código sirve UNA sola vez. Guárdalos en un lugar seguro, fuera del correo.'
       : 'Each code works ONCE. Keep them somewhere safe, outside your email.',
  ].join('\n');
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch (e) {
      console.warn('[Autenticador] no se pudo copiar:', e?.message);
    }
  };
  const descargar = () => {
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'deskte-codigos-de-recuperacion.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-green,#00ff88)', fontWeight: 700, marginBottom: '.5rem' }}>
        <ShieldCheck size={16} /> {es ? 'Autenticador activado' : 'Authenticator enabled'}
      </div>
      <div style={{ fontSize: '.84rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.6, marginBottom: '.75rem' }}>
        {es
          ? 'Guarda estos 10 códigos de recuperación. Si pierdes el teléfono, cada uno te deja entrar UNA vez. No los vas a volver a ver. No los guardes en tu correo.'
          : "Save these 10 recovery codes. If you lose your phone, each one lets you in ONCE. You won't see them again. Don't keep them in your email."}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '.4rem',
        background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 9, padding: '.75rem', fontFamily: 'var(--font-mono, monospace)', fontSize: '.95rem',
        color: 'var(--c-white,#eef6ff)', marginBottom: '.75rem',
      }}>
        {codigos.map((c) => <div key={c} style={{ textAlign: 'center' }}>{c}</div>)}
      </div>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button type="button" onClick={copiar} style={botonSecundario}>
          <Copy size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
          {copiado ? (es ? 'Copiados' : 'Copied') : (es ? 'Copiar' : 'Copy')}
        </button>
        <button type="button" onClick={descargar} style={botonSecundario}>
          <Download size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
          {es ? 'Descargar' : 'Download'}
        </button>
      </div>
      <button type="button" onClick={onListo} style={botonPrincipal}>
        {es ? 'Ya los guardé' : "I've saved them"}
      </button>
    </div>
  );
}

/**
 * `onActivado`: solo lo pasa la pantalla de alta obligatoria. Se llama cuando
 * la persona ya guardó sus códigos (o si resulta que ya lo tenía activo), para
 * que AuthGate abra el CRM.
 */
export function AutenticadorPanel({ lang, email, onActivado }) {
  const es = lang === 'es';
  const [estado, setEstado] = useState(null);     // { enabled, verified, recoveryCodesLeft }
  const [errCarga, setErrCarga] = useState(null);
  // Paso de la configuración: null | 'qr' | 'codigos' | 'quitar'
  const [paso, setPaso] = useState(null);
  const [alta, setAlta] = useState(null);         // { otpauthUrl, secret }
  const [codigo, setCodigo] = useState('');
  const [codigos, setCodigos] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [err, setErr] = useState(null);

  const cargar = () => {
    setErrCarga(null);
    return getMfaStatus()
      .then((s) => setEstado(s))
      .catch((e) => setErrCarga(e.message));
  };
  useEffect(() => { cargar(); }, []);
  // En la pantalla obligatoria, si ya estaba activo (otra pestaña), se sigue.
  useEffect(() => {
    if (onActivado && estado?.enabled && paso !== 'codigos') onActivado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado?.enabled]);

  const empezar = async () => {
    setErr(null); setOcupado(true);
    try {
      const d = await startMfaEnroll();
      setAlta(d); setCodigo(''); setPaso('qr');
    } catch (e) {
      setErr(mensajeDeError(e, es));
    } finally {
      setOcupado(false);
    }
  };

  const confirmar = async (ev) => {
    ev?.preventDefault();
    if (codigo.length !== 6) { setErr(es ? 'Escribe los 6 dígitos de la app.' : 'Enter the 6 digits from the app.'); return; }
    setErr(null); setOcupado(true);
    try {
      const d = await confirmMfaEnroll(codigo);
      setCodigos(d.recoveryCodes || []);
      setAlta(null); setCodigo(''); setPaso('codigos');
    } catch (e) {
      setErr(mensajeDeError(e, es));
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (ev) => {
    ev?.preventDefault();
    if (codigo.length !== 6) { setErr(es ? 'Escribe los 6 dígitos de la app.' : 'Enter the 6 digits from the app.'); return; }
    setErr(null); setOcupado(true);
    try {
      await disableMfa(codigo);
      setCodigo(''); setPaso(null);
      await cargar();
    } catch (e) {
      setErr(mensajeDeError(e, es));
    } finally {
      setOcupado(false);
    }
  };

  const caja = { background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1.5rem', marginTop: '1rem' };
  const titulo = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
      <Smartphone size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
      <div style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
        {es ? 'App autenticadora' : 'Authenticator app'}
      </div>
    </div>
  );

  if (errCarga) {
    return <div style={caja}>{titulo}<div style={cajaError}>{es ? 'Error al cargar:' : 'Failed to load:'} {errCarga}</div></div>;
  }
  if (!estado) {
    return <div style={caja}>{titulo}<div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>{es ? 'Cargando...' : 'Loading...'}</div></div>;
  }

  if (paso === 'codigos' && codigos) {
    return (
      <div style={caja}>
        {titulo}
        <CodigosDeRecuperacion codigos={codigos} es={es} email={email}
          onListo={() => { setCodigos(null); setPaso(null); if (onActivado) onActivado(); else cargar(); }} />
      </div>
    );
  }

  if (paso === 'qr' && alta) {
    return (
      <div style={caja}>
        {titulo}
        <ol style={{ fontSize: '.84rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.7, paddingLeft: '1.1rem', margin: '0 0 1rem' }}>
          <li>{es ? 'Abre Microsoft Authenticator o Google Authenticator en tu teléfono.' : 'Open Microsoft Authenticator or Google Authenticator on your phone.'}</li>
          <li>{es ? 'Toca "+" (Agregar cuenta) y escanea este código.' : 'Tap "+" (Add account) and scan this code.'}</li>
          <li>{es ? 'Escribe aquí el número de 6 dígitos que aparece.' : 'Type the 6-digit number it shows.'}</li>
        </ol>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '.75rem' }}>
          <QrDelEnlace url={alta.otpauthUrl} size={190} etiqueta={es ? 'Código QR del autenticador' : 'Authenticator QR code'} />
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', marginBottom: '1rem', wordBreak: 'break-all' }}>
          {es ? '¿No puedes escanear? Escribe esta clave en la app:' : "Can't scan? Enter this key in the app:"}
          <div style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', marginTop: 4, letterSpacing: '.08em' }}>
            {alta.secret.replace(/(.{4})/g, '$1 ').trim()}
          </div>
        </div>
        <form onSubmit={confirmar}>
          <input value={codigo} onChange={(e) => setCodigo(soloDigitos(e.target.value))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="000000" style={campoCodigo} autoFocus />
          {err && <div style={{ ...cajaError, marginTop: '.6rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem' }}>
            <button type="submit" disabled={ocupado} style={{ ...botonPrincipal, opacity: ocupado ? .6 : 1 }}>
              {ocupado ? (es ? 'Comprobando...' : 'Checking...') : (es ? 'Activar' : 'Turn on')}
            </button>
            <button type="button" onClick={() => { setPaso(null); setAlta(null); setErr(null); }} style={botonSecundario}>
              {es ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (!estado.enabled) {
    return (
      <div style={caja}>
        {titulo}
        <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.6, marginBottom: '1rem' }}>
          {es
            ? 'Protege tu cuenta aunque alguien consiga tu correo o tu contraseña: al entrar desde un navegador nuevo, Deskte te pedirá el código de 6 dígitos que genera la app en tu teléfono.'
            : 'Protect your account even if someone gets your email or password: when you sign in from a new browser, Deskte will ask for the 6-digit code the app on your phone generates.'}
        </div>
        <div style={{ fontSize: '.75rem', color: 'var(--c-amber,#ffb700)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: '1rem' }}>
          <ShieldAlert size={14} /> {es ? 'No está activado.' : 'Not enabled.'}
        </div>
        {err && <div style={{ ...cajaError, marginBottom: '.75rem' }}>{err}</div>}
        <button type="button" onClick={empezar} disabled={ocupado} style={{ ...botonPrincipal, opacity: ocupado ? .6 : 1 }}>
          {es ? 'Configurar el autenticador' : 'Set up authenticator'}
        </button>
      </div>
    );
  }

  return (
    <div style={caja}>
      {titulo}
      <div style={{ fontSize: '.85rem', color: 'var(--c-green,#00ff88)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: '.5rem' }}>
        <ShieldCheck size={15} /> {es ? 'Activado. Cada sesión nueva pide el código de tu teléfono.' : 'Enabled. Every new session asks for the code on your phone.'}
      </div>
      <div style={{ fontSize: '.78rem', color: estado.recoveryCodesLeft <= 3 ? 'var(--c-amber,#ffb700)' : 'var(--c-text3,#45688a)', marginBottom: '1rem' }}>
        {es
          ? `Te quedan ${estado.recoveryCodesLeft} códigos de recuperación.`
          : `${estado.recoveryCodesLeft} recovery codes left.`}
        {estado.recoveryCodesLeft <= 3 && (es
          ? ' Para tener 10 nuevos, quítalo y vuelve a configurarlo.'
          : ' To get 10 new ones, turn it off and set it up again.')}
      </div>
      {paso === 'quitar' ? (
        <form onSubmit={quitar}>
          <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.5rem' }}>
            {es ? 'Para quitarlo, escribe el código que muestra la app ahora:' : 'To turn it off, enter the code the app shows now:'}
          </div>
          <input value={codigo} onChange={(e) => setCodigo(soloDigitos(e.target.value))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="000000" style={campoCodigo} autoFocus />
          {err && <div style={{ ...cajaError, marginTop: '.6rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem' }}>
            <button type="submit" disabled={ocupado} style={{ ...botonPrincipal, background: 'linear-gradient(135deg,#cc3355,#991133)', opacity: ocupado ? .6 : 1 }}>
              {es ? 'Quitar el autenticador' : 'Turn off authenticator'}
            </button>
            <button type="button" onClick={() => { setPaso(null); setCodigo(''); setErr(null); }} style={botonSecundario}>
              {es ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={() => { setPaso('quitar'); setCodigo(''); setErr(null); }} style={botonSecundario}>
          {es ? 'Quitar el autenticador' : 'Turn off authenticator'}
        </button>
      )}
    </div>
  );
}

/**
 * Obligatorio para todos (Platform Admin) y esta persona aún no lo tiene: se
 * configura aquí, antes de ver nada del CRM. Salir es la única otra puerta.
 */
export function AutenticadorAltaScreen({ lang, email, onDone, onLogout }) {
  const es = lang === 'es';
  return (
    <div style={{
      minHeight: '100vh', background: '#070d14', color: '#eef6ff',
      fontFamily: 'Sora, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem',
    }}>
      <div style={{ maxWidth: 480, width: '100%' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.2rem', margin: '0 0 .5rem', textAlign: 'center' }}>
          {es ? 'Protege tu cuenta' : 'Protect your account'}
        </h2>
        <p style={{ color: '#a5b4fc', fontSize: '.86rem', lineHeight: 1.55, margin: '0 0 .5rem', textAlign: 'center' }}>
          {es
            ? 'Deskte ahora pide un código de una app autenticadora en tu teléfono. Instala Microsoft Authenticator o Google Authenticator (gratis) y configúralo aquí. Solo toma un minuto.'
            : 'Deskte now asks for a code from an authenticator app on your phone. Install Microsoft Authenticator or Google Authenticator (free) and set it up here. It only takes a minute.'}
        </p>
        <AutenticadorPanel lang={lang} email={email} onActivado={onDone} />
        <div style={{ textAlign: 'center' }}>
          <button type="button" onClick={onLogout}
            style={{ background: 'none', border: 'none', color: '#45688a', fontSize: '.75rem', marginTop: '1rem', cursor: 'pointer' }}>
            {es ? 'Cerrar sesión' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * La pantalla que tapa el CRM hasta que esta sesión dé el código. Salir
 * (cerrar sesión) es la única otra puerta.
 */
export function AutenticadorCodigoScreen({ lang, email, onVerified, onLogout }) {
  const es = lang === 'es';
  const [modoRecuperacion, setModoRecuperacion] = useState(false);
  const [valor, setValor] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [err, setErr] = useState(null);
  const enviando = useRef(false);

  // ¿Esta sesión ya dio el código? Pasa con varias pestañas: comparten la
  // sesión, y al dar el código en una, las demás seguían con esta pantalla —y
  // si se repetía el mismo código, salía "incorrecto" porque ya se había
  // gastado (Lissette 2026-09-27, "me sigue pidiendo el código"). Se pregunta
  // al abrir, al volver a la pestaña y cada pocos segundos.
  const avisado = useRef(false);
  useEffect(() => {
    let vivo = true;
    const mirar = async () => {
      // Una pestaña escondida no pregunta: con varias abiertas, sus vueltas
      // se sumaban al límite por minuto de la cuenta (Lissette 2026-10-07).
      if (document.visibilityState === 'hidden') return;
      try {
        const s = await getMfaStatus();
        if (vivo && !avisado.current && s && (s.verified || !s.enabled)) {
          avisado.current = true;
          onVerified();
        }
      } catch (e) {
        // Sin red o sesión cambiada: la pantalla se queda y el botón sigue
        // funcionando; la próxima vuelta lo vuelve a intentar.
        console.warn('[Autenticador] no se pudo comprobar la sesión:', e?.message);
      }
    };
    mirar();
    const iv = setInterval(mirar, 10000);
    const alVolver = () => { if (document.visibilityState === 'visible') mirar(); };
    // Solo 'visibilitychange': 'focus' llegaba a la vez y preguntaba dos veces.
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vivo = false;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', alVolver);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enviar = async (ev) => {
    ev?.preventDefault();
    if (enviando.current) return;
    const v = valor.trim();
    if (!modoRecuperacion && v.length !== 6) { setErr(es ? 'Escribe los 6 dígitos.' : 'Enter the 6 digits.'); return; }
    if (modoRecuperacion && v.replace(/[^a-z0-9]/gi, '').length !== 8) {
      setErr(es ? 'El código de recuperación tiene 8 caracteres (xxxx-xxxx).' : 'Recovery codes have 8 characters (xxxx-xxxx).');
      return;
    }
    enviando.current = true;
    setErr(null); setOcupado(true);
    try {
      await verifyMfa(modoRecuperacion ? { recoveryCode: v } : { code: v });
      if (!avisado.current) { avisado.current = true; onVerified(); }
    } catch (e) {
      setErr(mensajeDeError(e, es));
      setValor('');
    } finally {
      enviando.current = false;
      setOcupado(false);
    }
  };

  // Con 6 dígitos escritos (o pegados) se envía solo: es lo que espera quien
  // viene de otra app con código.
  useEffect(() => {
    if (!modoRecuperacion && valor.length === 6 && !ocupado) enviar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, modoRecuperacion]);

  return (
    <div style={{
      minHeight: '100vh', background: '#070d14', color: '#eef6ff',
      fontFamily: 'Sora, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem',
    }}>
      <form onSubmit={enviar} style={{
        maxWidth: 400, width: '100%', background: '#112538',
        border: '1px solid rgba(0, 212, 255, 0.25)', borderRadius: 14,
        padding: '2rem 1.75rem', textAlign: 'center', boxShadow: '0 24px 60px rgba(0, 0, 0, 0.4)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '.5rem', color: '#00d4ff' }}>
          {modoRecuperacion ? <KeyRound size={36} /> : <Smartphone size={36} />}
        </div>
        <h2 style={{ fontWeight: 700, fontSize: '1.15rem', margin: '0 0 .5rem' }}>
          {es ? 'Verifica que eres tú' : "Verify it's you"}
        </h2>
        <p style={{ color: '#a5b4fc', fontSize: '.86rem', lineHeight: 1.55, margin: '0 0 1.25rem' }}>
          {modoRecuperacion
            ? (es ? 'Escribe uno de tus códigos de recuperación. Cada uno sirve una sola vez.'
                  : 'Enter one of your recovery codes. Each one works only once.')
            : (es ? 'Abre tu app autenticadora y escribe el código de 6 dígitos de Deskte.'
                  : 'Open your authenticator app and enter the 6-digit Deskte code.')}
        </p>
        {email && <div style={{ fontSize: '.75rem', color: '#7da0c4', marginBottom: '.75rem' }}>{email}</div>}
        <input
          key={modoRecuperacion ? 'rec' : 'app'}
          value={valor}
          onChange={(e) => setValor(modoRecuperacion ? e.target.value.slice(0, 12) : soloDigitos(e.target.value))}
          inputMode={modoRecuperacion ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          placeholder={modoRecuperacion ? 'xxxx-xxxx' : '000000'}
          autoFocus
          style={{ ...campoCodigo, background: '#0b1826', border: '1px solid #1a3550', color: '#eef6ff' }}
        />
        {err && <div style={{ ...cajaError, marginTop: '.75rem', textAlign: 'left' }}>{err}</div>}
        <button type="submit" disabled={ocupado} style={{ ...botonPrincipal, width: '100%', marginTop: '1rem', padding: '.75rem', opacity: ocupado ? .6 : 1 }}>
          {ocupado ? (es ? 'Comprobando...' : 'Checking...') : (es ? 'Continuar' : 'Continue')}
        </button>
        <button type="button" onClick={() => { setModoRecuperacion((m) => !m); setValor(''); setErr(null); }}
          style={{ background: 'none', border: 'none', color: '#7aa3c0', fontSize: '.78rem', marginTop: '1rem', cursor: 'pointer', textDecoration: 'underline' }}>
          {modoRecuperacion
            ? (es ? 'Usar el código de la app' : 'Use the app code')
            : (es ? '¿Perdiste el teléfono? Usa un código de recuperación' : 'Lost your phone? Use a recovery code')}
        </button>
        <div>
          <button type="button" onClick={onLogout}
            style={{ background: 'none', border: 'none', color: '#45688a', fontSize: '.75rem', marginTop: '.5rem', cursor: 'pointer' }}>
            {es ? 'Cerrar sesión' : 'Sign out'}
          </button>
        </div>
      </form>
    </div>
  );
}
